import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  ApolloLink,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { fetchAuthSession } from 'aws-amplify/auth';
import { resolveAppContextForApiRequest } from '../portal/clientPortal.util';
import {
  readCurrentUserRole,
  readUserStoreSnapshot,
} from '../user/userStoreRef';
import { handleError } from '../util/utils';
import { createUploadFileNetworkErrorLink } from '../util/uploadFileNetworkErrorLink';
import { RestLink } from 'apollo-link-rest';

const restErrorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors || networkError) {
    console.log('[Apollo REST] Error Details:', {
      operation: operation?.operationName,
      variables: operation?.variables,
      graphQLErrors,
      networkError,
      statusCode:
        networkError && 'statusCode' in networkError
          ? networkError.statusCode
          : undefined,
      errorResponse:
        networkError && 'result' in networkError
          ? networkError.result
          : undefined,
    });
    handleError(
      graphQLErrors || networkError,
      '[Apollo REST] Errors: ' +
        JSON.stringify({
          operation: operation?.operationName,
          variables: operation?.variables,
        }),
    );
  }
});

// Add logging for REST requests
const restLoggingLink = new ApolloLink((operation, forward) => {
  console.log(`[Apollo REST] Request: ${operation.operationName}`, {
    operation: operation.operationName,
    variables: operation.variables,
  });

  return forward(operation).map((response) => {
    console.log(`[Apollo REST] Response: ${operation.operationName}`, {
      operation: operation.operationName,
      variables: operation.variables,
      response: response.data,
    });
    return response;
  });
});

// Error handling link
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ),
    );
  if (networkError) console.error(`[Network error]: ${networkError}`);
});

const uploadFileNetworkErrorLink = createUploadFileNetworkErrorLink(
  (message) => {
    console.error(message);
  },
);

const restUri = process.env.VITE_API_URL;
const graphqlUri = `${restUri}/graphql`;

const resolveAppContextForRequest = (): string => {
  const role = readCurrentUserRole();
  return resolveAppContextForApiRequest(role);
};

// Global tenant ID - will be set by the tenant service
let globalTenantId: string | null = null;

// HTTP connection to the API
const httpLink = createHttpLink({
  uri: graphqlUri,
  credentials: 'same-origin',
  // headers,
});

// Create REST link with dynamic tenant context
const createRestLink = () =>
  new RestLink({
    uri: restUri,
    headers: {
      'app-context': resolveAppContextForRequest(),
      'Tenant-Context': globalTenantId || '',
    },
  });

const restLink = createRestLink();

// Function to set the global tenant ID
export const setGlobalTenantId = (tenantId: string | null) => {
  globalTenantId = tenantId;
  console.log(`Global tenant ID set to: ${tenantId}`);
};

// Auth middleware with tenant context
const authLink = setContext(async (_, { headers }) => {
  try {
    const { tokens } = await fetchAuthSession();
    const { impersonatedUserId } = readUserStoreSnapshot();
    return {
      headers: {
        ...headers,
        Authorization: tokens?.idToken?.toString() || '',
        'Content-Type': 'application/json',
        'app-context': resolveAppContextForRequest(),
        'Tenant-Context': globalTenantId || '',
        ...(impersonatedUserId && { impersonate: impersonatedUserId }),
      },
    };
  } catch (error: any) {
    console.error('Auth error details:', {
      name: error?.name,
      message: error?.message,
      stack: error?.stack,
      fullError: error,
    });
    return {
      headers: {
        ...headers,
        'Content-Type': 'application/json',
        'Tenant-Context': globalTenantId || '',
      },
    };
  }
});

// Apollo Client instance
export const client = new ApolloClient({
  link: from([errorLink, uploadFileNetworkErrorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
  connectToDevTools: true, // For debugging
});

// Custom fetch wrapper for Apollo with tenant context
const customFetch = async (input: RequestInfo | URL, options?: RequestInit) => {
  const response = await fetch(input, {
    ...options,
    credentials: 'same-origin',
    mode: 'cors',
    headers: {
      ...options?.headers,
      'Tenant-Context': globalTenantId || '',
      'Referrer-Policy': 'no-referrer-when-cross-origin',
    },
  });
  return response;
};

// Update httpLink with custom fetch
const httpLinkWithCustomFetch = createHttpLink({
  uri: '/api/graphql',
  fetch: customFetch,
  credentials: 'same-origin',
});

// Update the client export to use the custom fetch
export const clientWithCustomFetch = new ApolloClient({
  link: from([
    errorLink,
    uploadFileNetworkErrorLink,
    authLink,
    httpLinkWithCustomFetch,
  ]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
  connectToDevTools: true,
});

// Setup rest client with logging
export const restClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: restLoggingLink.concat(restErrorLink).concat(restLink),
});
